// PromptPage.js - PROMPT-ONLY LANDING INPUT
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './PromptPage.css';
import { Zap, Crown, Briefcase, Sparkles } from 'lucide-react';
import HelloSentences from '../essentials/HelloSentences';
import { cleanUserInput, validatePromptInput } from '../constants/inputSanitizer';
import { useAuth } from '../AuthContext';
import Logo from '../assets/logo-sv.png';
import { promptExamples } from '../constants/promptExamples';

function PromptPage({
  onPromptGenerate,
  onLoadPresentation,
  isProcessing,
  isFetchingImages,
  processingError,
  moderationBlock,
  userPlan
}) {

  const { user } = useAuth();
  const location = useLocation();

  const [promptText, setPromptText] = useState('');
  const [validationAttempts, setValidationAttempts] = useState(0);
  const [validationError, setValidationError] = useState(null);
  const [showExamplesMenu, setShowExamplesMenu] = useState(false);

  // userPlan comes from the parent's single shared planTracker instance
  // (useSlideGenerator -> usePlanTracker). Don't call usePlanTracker()
  // again here — that was a second, out-of-sync copy of the same data.
  const currentUserPlan = userPlan;

  const maxLength = 5000;
  const charCount = promptText.length;

  // ── Handle incoming state from landing page hero box ──────────────────────
  useEffect(() => {
    const incoming = location.state;
    if (!incoming?.autoSubmit) return;

    // Clear so a page refresh won't re-trigger
    window.history.replaceState({}, document.title);

    if (incoming.heroPrompt) {
      // Text prompt only — pre-fill and auto-submit
      setPromptText(incoming.heroPrompt);
      setTimeout(() => {
        const cleaned = incoming.heroPrompt.trim();
        if (cleaned.length >= 3) {
          onPromptGenerate(cleaned);
        }
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only
  // ─────────────────────────────────────────────────────────────────────────

  const handlePromptSubmit = () => {
    if (!user) {
      setValidationError({
        message: 'Please sign in to generate slides',
        showExamples: false
      });
      return;
    }
    setValidationError(null);

    const cleaned = cleanUserInput(promptText, {
      preserveNewlines: false,
      removeInvisibleChars: true,
      normalizeUnicode: true,
      trimWhitespace: true,
      maxConsecutiveSpaces: 1
    });

    const validation = validatePromptInput(cleaned, {
      maxLength: 5000
    });

    if (!validation.isValid) {
      setValidationAttempts(prev => prev + 1);

      const errorMessages = {
        'TOO_SHORT': 'Please enter a topic (at least 3 characters)',
        'TOO_LONG': 'Prompt is too long. Please keep it under 5000 characters.',
        'NO_WORDS': 'Please enter a topic with actual words',
        'REPEATED_CHAR': 'Please enter a meaningful topic',
        'KEYBOARD_SPAM': 'Please enter a real topic, not random keyboard input',
        'NO_CONTENT': 'Please enter actual content',
        'ONLY_NUMBERS': 'Please describe your topic in words, not just numbers',
        'SINGLE_WORD': 'Please provide more details about your topic',
        'TOO_MANY_SPECIAL_CHARS': 'Please use fewer special characters'
      };

      const message = errorMessages[validation.reason] || 'Invalid input';

      setValidationError({
        message,
        showExamples: validationAttempts >= 2
      });
      return;
    }

    setValidationAttempts(0);
    onPromptGenerate(cleaned);
  };

  const handlePromptKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handlePromptSubmit();
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;

    if (newValue.length > maxLength) {
      return;
    }

    setPromptText(newValue);

    if (validationError) {
      setValidationError(null);
    }

    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 250) + 'px';
  };

  const handleExampleClick = (example) => {
    setPromptText(example);
    setShowExamplesMenu(false);

    setTimeout(() => {
      const textarea = document.querySelector('.prompt-textarea');
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 250) + 'px';
      }
    }, 0);
  };

  const currentPlan = currentUserPlan || 'free';

  return (
    <div className="claude-container">
      <div className="content-wrapper">

        {user && (
          <div className="user-plan-display">
            <div className="plan-info-badge">
              {currentPlan === 'starter' && <Zap size={16} />}
              {currentPlan === 'creator' && <Crown size={16} />}
              {currentPlan === 'professional' && <Briefcase size={16} />}
              {currentPlan === 'free' && <Sparkles size={16} />}
              <span className="plan-tier-name">
                {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan
              </span>
              {currentPlan === 'free' && (
                <a
                  href="/pricing-plans"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="plan-upgrade-link"
                >
                  .Upgrade
                </a>
              )}
            </div>
          </div>
        )}

        <HelloSentences />

        {/* PROMPT BOX */}
        <div className="prompt-box">
          <div className="textarea-wrapper">
            <textarea
              className="prompt-textarea"
              placeholder="Describe your presentation topic... (e.g., 'Introduction to Machine Learning', 'Benefits of Remote Work', 'History of Ancient Rome')"
              value={promptText}
              onChange={handleTextChange}
              onKeyDown={handlePromptKeyDown}
              maxLength={maxLength}
              disabled={isProcessing}
              rows="1"
            />

            <div className="textarea-actions">
              <button
                className="settings-btn"
                onClick={() => setShowExamplesMenu(!showExamplesMenu)}
                disabled={isProcessing}
                title="Prompt Use Cases"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                  <path d="M40,88H73a32,32,0,0,0,62,0h81a8,8,0,0,0,0-16H135a32,32,0,0,0-62,0H40a8,8,0,0,0,0,16Zm64-24A16,16,0,1,1,88,80,16,16,0,0,1,104,64ZM216,168H199a32,32,0,0,0-62,0H40a8,8,0,0,0,0,16h97a32,32,0,0,0,62,0h17a8,8,0,0,0,0-16Zm-48,24a16,16,0,1,1,16-16A16,16,0,0,1,168,192Z"></path>
                </svg>
              </button>

              <button
                className="send-btn"
                onClick={handlePromptSubmit}
                disabled={
                  !user ||
                  isProcessing ||
                  promptText.trim().length === 0
                }
                title={!user ? "Please sign in to generate slides" : "Generate Slides"}
              >
                {isProcessing ? (
                  <div className="spinner-small"></div>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 19V5M5 12l7-7 7 7"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Generating — shown inline below the prompt box until slides come back */}
        {(isProcessing || isFetchingImages) && (
          <div className="loading-state-inline">
            <div className="loading-content-inline">
              <img src={Logo} alt="Logo" className="loading-logo" />
              <div className="loading-text">
                <h3>{isProcessing ? 'Generating your slides...' : 'Finding images...'}</h3>
                <p>This may take a few moments</p>
              </div>
            </div>
          </div>
        )}

        {/* AI moderation block */}
        {moderationBlock && (
          <div className="moderation-block-card">
            <div className="moderation-block-header">
              <span className="block-icon">🚫</span>
              <h3>Content Cannot Be Processed</h3>
            </div>

            <div className="moderation-block-content">
              <p className="block-reason">
                <strong>Reason:</strong> {moderationBlock.reason}
              </p>

              {moderationBlock.suggestion && (
                <div className="block-suggestions">
                  <p className="suggestions-title">💡 Educational Alternatives:</p>
                  <div className="suggestions-text">
                    {moderationBlock.suggestion}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Char limit indicator */}
        {charCount > maxLength * 0.9 && (
          <div className="char-limit-indicator">
            <span className={charCount >= maxLength ? 'char-count-exceeded' : 'char-count-warning'}>
              {charCount.toLocaleString()} / {maxLength.toLocaleString()} characters
            </span>
          </div>
        )}

        {/* Examples panel */}
        {showExamplesMenu && (
          <div className="examples-panel">
            <div className="examples-panel-header">
              <span className="examples-panel-title">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                  <path d="M40,88H73a32,32,0,0,0,62,0h81a8,8,0,0,0,0-16H135a32,32,0,0,0-62,0H40a8,8,0,0,0,0,16Zm64-24A16,16,0,1,1,88,80,16,16,0,0,1,104,64ZM216,168H199a32,32,0,0,0-62,0H40a8,8,0,0,0,0,16h97a32,32,0,0,0,62,0h17a8,8,0,0,0,0-16Zm-48,24a16,16,0,1,1,16-16A16,16,0,0,1,168,192Z"></path>
                </svg>
                {' '}Prompt Use Cases
              </span>
              <button
                className="examples-close-btn"
                onClick={() => setShowExamplesMenu(false)}
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="examples-panel-content">
              {promptExamples.map((category, idx) => (
                <div key={idx} className="examples-category">
                  <div className="examples-category-title">{category.category}</div>
                  {category.examples.map((example, exIdx) => (
                    <button
                      key={exIdx}
                      className="example-item"
                      onClick={() => handleExampleClick(example)}
                      disabled={isProcessing}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Validation errors */}
        {validationError && (
          <div className="validation-error-card">
            <div className="validation-error-content">
              <img src={Logo} alt="Logo" className="validation-error-logo" />
              <div className="validation-error-text">
                <p className="validation-error-message">{validationError.message}</p>

                {validationError.showExamples && (
                  <div className="validation-examples">
                    <p className="validation-examples-title">Need help? Try examples like:</p>
                    <ul>
                      <li>"Introduction to Machine Learning"</li>
                      <li>"Benefits of Remote Work"</li>
                      <li>"History of Ancient Rome"</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Processing errors */}
        {processingError && (
          <div className="error-message">
            <span>⚠️</span>
            <span>{processingError}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PromptPage;